import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import FAQAccordion from '../components/FAQAccordion';
import { FAQPageSchema, BreadcrumbSchema, integrationsFaqItems } from '../components/JsonLdSchema';

const sections = [
  {
    title: 'Food Aggregators',
    desc: 'Automate online orders with direct injection. Manage Zomato, Swiggy, and Magicpin from your POS with real-time menu sync and status updates.',
    items: [
      { name: 'Zomato', desc: 'Direct order injection and instant menu updates across all locations.', tag: 'Menu Sync', tagColor: '#10b981' },
      { name: 'Swiggy', desc: 'Real-time status tracking and seamless inventory mapping to prevent stockouts.', tag: 'Orders Injection', tagColor: '#f97316' },
      { name: 'Magicpin', desc: 'Streamlined voucher redemption and automated reconciliation for every order.', tag: 'Reconciliation', tagColor: '#8b5cf6' },
    ]
  },
  {
    title: 'Payment Gateways',
    desc: 'Accept payments securely with trusted providers. Automated settlement mapping keeps your accounts accurate and your team confident.',
    items: [
      { name: 'Razorpay', desc: 'Fast and secure settlements with unified payment reporting.' },
      { name: 'Pine Labs', desc: 'Seamless terminal integration for high-speed offline checkouts.' },
      { name: 'Paytm', desc: 'Instant QR payments with real-time payment status updates.' },
      { name: 'UPI Providers', desc: 'Zero-MDR flows via GPay, PhonePe, and BharatPe QR.' },
    ]
  },
  {
    title: 'Accounting & Reporting',
    desc: 'Make reconciliation easy. Export reports or sync with tools your accountant already uses.',
    items: [
      { name: 'GST Reports', desc: 'Tax-ready sales summaries and downloadable invoices for compliance workflows.', tag: 'Export', tagColor: '#3b82f6' },
      { name: 'Tally (CSV)', desc: 'Clean export formats for Tally imports and accounting reconciliation.', tag: 'CSV/Excel', tagColor: '#6366f1' },
      { name: 'Daily P&L', desc: 'Auto summaries for sales, discounts, taxes and settlement breakdowns.', tag: 'Auto Reports', tagColor: '#10b981' },
    ]
  },
  {
    title: 'Hardware & Devices',
    desc: 'Reliable POS hardware support—printers, KDS, scanners, and cash drawers to keep billing fast.',
    items: [
      { name: 'Thermal Printers', desc: 'Quick bill prints with configurable headers, GST and QR details.', tag: 'Billing', tagColor: '#6b7280' },
      { name: 'Kitchen Display (KDS)', desc: 'Send orders to kitchen screens with live status and prep time tracking.', tag: 'Live Status', tagColor: '#CC0000' },
      { name: 'Barcode & Weighing', desc: 'Barcode scanning + weighed items to speed up counter billing.', tag: 'Fast Checkout', tagColor: '#f59e0b' },
    ]
  },
];

function IntCard({ item }) {
  const [hov, setHov] = React.useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? '#fff' : '#f9fafb',
        border: `1px solid ${hov ? '#CC0000' : '#e5e7eb'}`,
        borderRadius: 12, padding: '24px 20px',
        transition: 'all 0.2s',
        boxShadow: hov ? '0 4px 20px rgba(204,0,0,0.1)' : 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{item.name}</h3>
        {item.tag && (
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 100,
            background: item.tagColor + '20', color: item.tagColor
          }}>{item.tag}</span>
        )}
      </div>
      <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{item.desc}</p>
    </div>
  );
}

export default function Integrations() {
  return (
    <main style={{ paddingTop: 64 }}>
      <SEOHead
        title="Integrations – Zomato, Swiggy, Payment Gateways | DPOS"
        description="Connect DPOS with Zomato, Swiggy, Magicpin, payment gateways and more to automate order flow, sync menus, and streamline restaurant operations."
        canonical="/integrations"
      />
      <FAQPageSchema items={integrationsFaqItems} />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Integrations' },
      ]} />
      {/* Hero */}
      <section style={{ padding: '72px 24px 64px', background: '#fff' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }} className="int-hero-grid">
          {/* Left: text */}
          <div>
            <h1 style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 800, color: '#111827', marginBottom: 16, lineHeight: 1.15 }}>
              Powerful Integrations to<br />Streamline Your Business
            </h1>
            <p style={{ fontSize: 17, color: '#6b7280', lineHeight: 1.7, marginBottom: 32 }}>
              Connect your favorite tools with DPOS to automate order flow, synchronize menus,
              and simplify payment settlements across all platforms.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {['Real-time Menu Sync', 'Automated Reconciliation', 'Multi-Outlet Ready'].map(tag => (
                <span key={tag} style={{
                  fontSize: 12, fontWeight: 600, padding: '4px 12px',
                  background: '#f3f4f6', color: '#374151', borderRadius: 100, border: '1px solid #e5e7eb'
                }}>{tag}</span>
              ))}
            </div>
          </div>

          {/* Right: integrations banner */}
          <div className="int-banner-grid" style={{ background: '#f9fafb', borderRadius: 20, border: '1px solid #e5e7eb', padding: 20, display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center' }}>
            {/* Food Aggregators */}
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Food Aggregators</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { name: 'Zomato',   logo: '/logo-zomato.png',   tag: 'Menu Sync',        tagColor: '#e53935' },
                  { name: 'Swiggy',   logo: '/logo-swiggy.png',   tag: 'Orders',           tagColor: '#f97316' },
                  { name: 'Magicpin', logo: '/logo-magicpin.png',  tag: 'Reconciliation',   tagColor: '#8b5cf6' },
                ].map(item => (
                  <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 10, padding: '7px 10px', border: '1px solid #e5e7eb' }}>
                    <img src={item.logo} alt={item.name} style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#111827', flex: 1 }}>{item.name}</span>
                    <span style={{ fontSize: 8, fontWeight: 700, color: item.tagColor, background: item.tagColor + '18', padding: '2px 5px', borderRadius: 100, whiteSpace: 'nowrap' }}>{item.tag}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Centre D logo */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#fff', border: '2px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(204,0,0,0.12)' }}>
                <img src="/logo-d.png" alt="DPOS" style={{ width: 36, height: 36, objectFit: 'contain' }} />
              </div>
            </div>

            {/* Payment Gateways */}
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Payment Gateways</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { name: 'Razorpay',  logo: '/logo-razorpay.png' },
                  { name: 'Pine Labs', logo: '/logo-pinelabs.png' },
                  { name: 'Paytm',    logo: '/logo-paytm.png' },
                  { name: 'UPI',      logo: '/logo-upi.png' },
                ].map(item => (
                  <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 10, padding: '7px 10px', border: '1px solid #e5e7eb' }}>
                    <img src={item.logo} alt={item.name} style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#111827' }}>{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration sections */}
      {sections.map(sec => (
        <section key={sec.title} style={{ padding: '56px 24px', borderTop: '1px solid #f3f4f6' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            <div style={{ marginBottom: 40, maxWidth: 600 }}>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: '#111827', marginBottom: 10 }}>{sec.title}</h2>
              <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7 }}>{sec.desc}</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              {sec.items.map(item => <IntCard key={item.name} item={item} />)}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section style={{ padding: '80px 24px', background: '#111827', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#fff', marginBottom: 16 }}>
            Ready to supercharge your restaurant operations?
          </h2>
          <p style={{ fontSize: 16, color: '#9ca3af', marginBottom: 36, lineHeight: 1.7 }}>
            Tell us what tools you use today—we'll recommend the best integration setup.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" style={{ background: '#CC0000', color: '#fff', padding: '12px 28px', borderRadius: 8, fontWeight: 600, fontSize: 15 }}>Talk to Sales</Link>
            <Link to="/contact" style={{ background: 'transparent', color: '#fff', padding: '12px 28px', borderRadius: 8, fontWeight: 600, fontSize: 15, border: '1px solid #374151' }}>Book a Free Demo</Link>
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 20 }}>Guides on Integrations and Ordering</h2>
          <ul style={{ display: 'flex', flexDirection: 'column', gap: 12, listStyle: 'none', padding: 0 }}>
            <li><Link to="/blog/how-qr-menu-ordering-works-restaurants" style={{ color: '#CC0000', fontWeight: 600 }}>How QR menu ordering works for restaurants</Link></li>
            <li><Link to="/blog/kot-printing-for-restaurants" style={{ color: '#CC0000', fontWeight: 600 }}>KOT printing for restaurants: how it works and how to set it up</Link></li>
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '80px 24px', background: '#f9fafb' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.12em', textAlign: 'center', marginBottom: 6 }}>
            Frequently Asked Questions
          </p>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#111827', textAlign: 'center', marginBottom: 40 }}>
            Integrations, Answered
          </h2>
          <FAQAccordion items={integrationsFaqItems} />
        </div>
      </section>
    </main>
  );
}
